// Reports Module v2 — Tiptap editor + Templates + Phrases
import { loadTemplates, saveTemplate, deleteTemplate } from './templates.js';
import { withClear } from '../search-clear/index.js';
import { injectStyles } from './styles.js';
import { generatePDFBlob } from './pdf-util.js'; // intake / generic docs (blocks) only
import { makeReportPdfBlob as _makePdfBlob } from './render/load.js'; // report PDFs: the v2 engine (lazy bundle)
import { brandingForClientRef, clientRefOf } from './clinic-branding.js';   // D-2 SC3: the clinic letterhead
import { fetchLogoOnce } from './render/logo.js';           // Phase 2 Q5: the logo's colours for Report Branding
import { resolveColours } from './render/colours.js';
// makeReportPdfBlob (below) is exported by name: the editor (tiptap bundle) imports it for Download
import { loadPhrases, savePhrase, deletePhrase } from './phrases.js';
import { rbAll, rbDel } from './draft-backup.js';
import { openSignaturePad } from './signature-pad.js'; // Phase 3 R9: draw a signature (Report Branding)
import { removableSignaturePaths, removableLogoPaths } from './signature-guard.js'; // R-2: the old signature files a save may remove

const T = (k, p) => window.HUD?.T?.(k, p) || k;
const _locDate = (d, o, l) => window.HUD?._locDate ? window.HUD._locDate(d, o, l) : d.toLocaleDateString(l, o);
const _locTime = (d, o, l) => window.HUD?._locTime ? window.HUD._locTime(d, o, l) : d.toLocaleTimeString(l, o);

const RS = {
  templates: [], templatesLoaded: false,
  reports: [], reportsLoaded: false,
  phrases: [], phrasesLoaded: false,
  monthlyCount: 0,
  view: 'hub',
  currentTemplate: null,
  currentReport: null,
  selectedChildId: null,
  returnToPatient: null,
  branding: null,
  brandingLoaded: false,
  brandingBannerDismissed: false,
};

const MONTHLY_LIMIT = 5;
const DEFAULT_BRANDING = { header_color: '#0d9488', font_style: 'sans-serif', header_style: 'compact', footer_text: 'Confidential \u2014 For Clinical Use Only' };

function H() { return window.HUD || {}; }
// F-19 (QP-6): without plan access Reports is READ-ONLY, never a lock card: lists, open (the editor forces read-only),
// download and delete (QP-2) work; New report / From template / Upload template / New template / Edit / Duplicate /
// New phrase / Save phrase pause and open the existing upgrade screen.
const _paid = fn => (H()._paidFn ? H()._paidFn('Reports', fn) : fn);
const _paidBtn = b => (H()._paidMark ? H()._paidMark(b) : b);
// F-110: the monthly limit binds ONLY a running trial (H()._reportsCapped, index.html): comped, exempt, paid and every other state are unlimited, and an entitlement that is not known yet is not capped.
// A missing bridge counts as 'not capped': an unknown plan must never block a paying user (the F-110 incident). The count is FINALIZED reports only (drafts do not count).
const _isCapped = () => (H()._reportsCapped ? !!H()._reportsCapped() : false);
const _overMonthlyLimit = () => _isCapped() && RS.monthlyCount >= MONTHLY_LIMIT;
// One refusal for every way to start a report (the hub, the patient tab, From template): the toast, then true.
const _capRefused = () => { if (!_overMonthlyLimit()) return false; H().toast(T('rb_monthly_limit_reached', { n: MONTHLY_LIMIT, max: MONTHLY_LIMIT }), 'error'); return true; };
// The read-only banner (ended: rb_readonly_banner; never tried: the trial text, QR-b A).
const _roBanner = () => (H()._readOnlyBanner ? H()._readOnlyBanner(T('rb_readonly_banner'), T('rb_trial_start_card'), 'Reports') : null);

// Get section IDs from either format: array of strings OR {ids:[], imported:[]}
function _getSectionIds(sections) {
  if (!sections) return [];
  if (Array.isArray(sections)) return sections;
  return sections.ids || [];
}

function _getSectionCount(sections) {
  return _getSectionIds(sections).length;
}

function _templateSectionCount(t) {
  if (t.content?.content?.length) return t.content.content.length;
  return _getSectionCount(t.sections);
}

export function _buildCredentials(session) {
  const parts = [];
  if (session?.credentials_title) parts.push(session.credentials_title);
  if (session?.credentials_certs) parts.push(session.credentials_certs);
  if (session?.credentials_license) parts.push('Lic. ' + session.credentials_license);
  return parts.join(', ');
}

export function _downloadBlob(blob, reportType, childName, reportName) {
  const rawName = reportName || reportType || 'Report';
  const name = rawName.replace(/[^a-zA-Z0-9 \u0590-\u05FF]/g, '').replace(/\s+/g, '_').slice(0, 50);
  const child = (childName || 'Patient').replace(/\s+/g, '_');
  const date = window.HUD._localToday();
  const filename = name + '_' + child + '_' + date + '.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function calcAge(dob) {
  if (!dob) return '';
  const b = new Date(dob), n = new Date();
  let y = n.getFullYear() - b.getFullYear(), m = n.getMonth() - b.getMonth();
  if (m < 0) { y--; m += 12; }
  return y + (y === 1 ? ' year' : ' years') + (m ? ' ' + m + (m === 1 ? ' month' : ' months') : '');
}

function getChildren() {
  const { DB } = H();
  const db = DB?.children || [], ls = (window.HUD?.LS?.get?.('children', []) || []);
  const seen = new Set(), out = [];
  [...db, ...ls].forEach(c => { if (!seen.has(c.id)) { seen.add(c.id); out.push(c); } });
  return out;
}

function statusBadge(status) {
  const { el } = H();
  const m = { finalized: ['rpt-badge-finalized', '✅ ' + T('rb_status_finalized')], generated: ['rpt-badge-generated', '📄 ' + T('rb_status_generated')], draft: ['rpt-badge-draft', '✏️ ' + T('rb_status_draft')] };
  const [cls, lbl] = m[status] || m.draft;
  return el('span', { class: 'rpt-badge ' + cls }, [lbl]);
}

function nav(view, step) {
  RS.view = view; RS.step = step || 0; RS.dirty = false;
  // If going to 'hub' and we came from a patient tab, go back to patient context instead.
  // Sub-commit 4f — handle pre-parent return separately to avoid S.activeChild corruption.
  if (view === 'hub' && RS.returnToSpecPatient) {
    const { S } = H();
    S.activeSpecPatient = RS.returnToSpecPatient;
    S.activeTab = 'spec-patient-detail';
    S.activeSpecPatientTab = 'Reports';
    RS.returnToSpecPatient = null;
  } else if (view === 'hub' && RS.returnToPatient) {
    const { S } = H();
    S.activeChild = RS.returnToPatient;
    S.activeTab = 'patient-reports';
    RS.returnToPatient = null;
  }
  H().re();
}

async function loadData() {
  const { _supa, session } = H();
  if (!_supa || !session) return;
  if (!RS.templatesLoaded) { RS.templates = await loadTemplates(); RS.templatesLoaded = true; }
  if (!RS.phrasesLoaded) { RS.phrases = await loadPhrases(); RS.phrasesLoaded = true; }
  if (!RS.reportsLoaded) {
    // F-45c: a private client's report is placed (clinic filter) and named from the private-client list; on a cleared
    // device the boot load may not have landed yet, so load it first (one owner-scoped select, only when not loaded).
    if (H()._spLoaded && !H()._spLoaded()) { try { await H()._loadSpecialistPatients?.(); } catch (e) { console.error('\u274c reports: load private clients:', e); } }
    try {
      const { data, error: _rErr } = await _supa.from('reports').select('*').eq('specialist_id', session.id).order('created_at', { ascending: false });
      if (_rErr) console.error('❌ load reports:', _rErr);
      RS.reports = data || [];
      const ms = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      RS.monthlyCount = RS.reports.filter(r => r.created_at >= ms && (r.status === 'finalized' || r.status === 'final')).length; // F-110: finalized only, drafts do not count
      RS.reportsLoaded = true;
      // R3f: placeholder rows need the real list to compare against — never classify from a failed load
      if (!_rErr) await _loadGhosts(); else RS.ghosts = [];
    } catch (e) { console.error('Load reports:', e); }
  }
  if (!RS.brandingLoaded) {
    try {
      const { data } = await _supa.from('report_settings').select('*').eq('specialist_id', session.id).limit(1);
      RS.branding = data?.[0] || null;
      RS.brandingLoaded = true;
    } catch (e) { console.error('Load branding:', e); RS.brandingLoaded = true; }
  }
}

export async function ensureBranding() {
  if (RS.brandingLoaded) return;
  const { _supa, session } = H();
  if (!_supa || !session) return;
  try {
    const { data } = await _supa.from('report_settings').select('*').eq('specialist_id', session.id).limit(1);
    RS.branding = data?.[0] || null;
    RS.brandingLoaded = true;
    console.log('[reports] Branding loaded on demand:', RS.branding?.practice_name || '(none)');
  } catch (e) { console.error('Load branding:', e); RS.brandingLoaded = true; }
}

// Phase 2 Q5 (D5): the colours extracted from a branding's logo (no override) — shown in Report Branding.
export async function reportColours(branding) {
  const { _supa } = H();
  const logo = await fetchLogoOnce(_supa, branding?.logo_storage_path);
  return { logo: resolveColours(logo), hasLogo: !!(logo && logo.rgba) };
}

// R-2 (QR2-3 B): a signature read failed — another tab or device replaced (and removed) the file this page still points
// at. Reload the settings ONCE and hand back the current path; a path that already had its one reload gets none
// (no loop) and the caller renders without a signature (❌ logged).
const _sigReloaded = new Set();
export async function recoverSignaturePath(failedPath) {
  if (!failedPath || _sigReloaded.has(failedPath)) return null;
  _sigReloaded.add(failedPath);
  RS.brandingLoaded = false;
  await ensureBranding();
  return getBranding().signature_path || null;
}
// …and when the retry with the current path fails too, that path gets no reload either (no loop).
export function signatureRetryFailed(path) { if (path) _sigReloaded.add(path); }

// Report PDFs (Preview, Download, Share, the finalized file): the v2 engine; when the signature didn't load, the same
// one-reload recovery, then one retry with the current settings.
// D-2 SC3: a client of a clinic gets the clinic letterhead (resolved in the database); the signature stays personal.
async function _withClinicLetterhead(ctx, personal) {
  const ref = clientRefOf(ctx);
  if (!ref) return personal;
  return brandingForClientRef(ctx.supa || H()._supa, personal, ref, DEFAULT_BRANDING);
}
export async function makeReportPdfBlob(ctx0) {
  const ctx = { ...ctx0, branding: await _withClinicLetterhead(ctx0, ctx0?.branding) };
  const res = await _makePdfBlob(ctx);
  const path = ctx?.branding?.signature_path;
  if (!path || res?.meta?.signature_sha256) return res;
  const cur = await recoverSignaturePath(path);
  if (cur) {
    const res2 = await _makePdfBlob({ ...ctx, branding: await _withClinicLetterhead(ctx, getBranding()) });
    if (res2?.meta?.signature_sha256) return res2;
    signatureRetryFailed(cur);
    console.error('❌ report signature unavailable after one settings reload; made without it');
    return res2;
  }
  console.error('❌ report signature unavailable; made without it');
  return res;
}

export function getBranding() {
  const result = { ...DEFAULT_BRANDING, ...(RS.branding || {}) };
  return result;
}

// ── Sub-commit 10: share-to-files helper (Steps 1-4 of the share chain) ──
// Generates the PDF, uploads to the patient's shared folder, INSERTs the files
// row, and optimistically pushes into DB.folders[].files[] + re().
// Returns { fileId, folderKey, destPath, filename } for the caller to use in
// notification linkData. Throws on any failure with the orphaned blob cleaned
// up automatically.
//
// Args (object):
//   reportRow   — the reports row (needs id, name?, report_type?, child_id, specialist_patient_id)
//   content     — the report's Tiptap JSON (the v2 engine renders it; never markdown / generated_text, D13)
//   supa        — Supabase client
//   sess        — current session (for uploaded_by + specialist info)
//   isPreParent — bool: pre-parent (Pattern G path) or connected child (Pattern F)
//   patientId   — child_id OR specialist_patient_id depending on isPreParent
//   folderKey   — destination folder key ('spec_<specialistId>' for connected; 'spec_<sp_id>' for pre-parent)
// Phase 2 Q4 (ruling 6): the stored final PDF (specialist-storage, the author's own folder) — one signed URL + one GET.
export async function readStoredReportPdf(supa, path) {
  const { data, error } = await supa.storage.from('specialist-storage').createSignedUrl(path, 60);
  if (error || !data?.signedUrl) { console.error('❌ stored report PDF signed URL:', error); throw new Error(error?.message || 'no signed URL'); }
  const r = await fetch(data.signedUrl);
  if (!r.ok) { console.error('❌ stored report PDF fetch:', r.status); throw new Error('HTTP ' + r.status); }
  return new Uint8Array(await r.arrayBuffer());
}

export async function _pdfToPatientFolder({ reportRow, content, supa, sess, isPreParent, patientId, folderKey, blob, filename }) {
  // `blob`+`filename` = caller supplies a prebuilt PDF (intake/generic docs), skipping report generation.
  if (!reportRow?.id && !blob) throw new Error('No report id');
  if (!patientId) throw new Error('No patient id');
  if (!folderKey) throw new Error('No folder key');
  const hud = H();
  const DB = hud.DB || {};
  const LS = hud.LS;

  // Step 1 — PDF: use the prebuilt blob if supplied (intake), else the v2 engine from the report's Tiptap content.
  let pdfBlob = blob;
  // Phase 2 Q4 (ruling 6): a report finalized with a stored PDF is shared as THAT file — never rendered again.
  if (!pdfBlob && reportRow?.pdf_path) pdfBlob = new Blob([await readStoredReportPdf(supa, reportRow.pdf_path)], { type: 'application/pdf' });
  if (!pdfBlob) {
    await ensureBranding();
    let child = null;
    if (isPreParent) {
      child = (DB.specialistPatients || []).find(sp => sp.id === patientId) || null;
    } else {
      child = (DB.children || []).find(c => c.id === patientId)
        || (LS?.get?.('children', []) || []).find(c => c.id === patientId)
        || null;
    }
    const doc = content || reportRow?.content;
    if (!doc) throw new Error('No report content');
    pdfBlob = (await makeReportPdfBlob({ content: doc, report: reportRow, patient: child, session: sess, branding: getBranding(), supa, clientRef: isPreParent ? { specialistPatientId: patientId, childId: null } : { specialistPatientId: null, childId: patientId } })).blob;
  }

  // Step 2 — Upload to storage (Pattern F for connected child, Pattern G for pre-parent).
  const fname = filename || ((reportRow?.name?.trim() || H().T?.('rpdf_title_default') || 'Report') + '.pdf'); // D8: never "general"
  const storagePrefix = isPreParent ? ('spec_patient/' + patientId) : patientId;
  const destPath = storagePrefix + '/' + Date.now() + '_' + (window.HUD?._safeStorageName?.(fname) || String(fname).replace(/[^a-zA-Z0-9._-]+/g, '_') || 'report.pdf');   // BUG5: ASCII-safe key; files.name keeps the original
  const { error: upErr } = await supa.storage.from('huddledin-files').upload(destPath, pdfBlob, { contentType: 'application/pdf', upsert: false });
  if (upErr) { console.error('❌ share-to-files upload:', upErr); throw upErr; }

  // Step 3 — files INSERT with XOR-aware FK + folder-matching category.
  const _fk = isPreParent
    ? { specialist_patient_id: patientId, child_id: null }
    : { child_id: patientId, specialist_patient_id: null };
  const { data: insRow, error: insErr } = await supa.from('files').insert({
    ..._fk,
    uploaded_by: sess.id,
    name: fname,
    storage_path: destPath,
    mime_type: 'application/pdf',
    size_bytes: pdfBlob.size || 0,
    category: folderKey,
  }).select('id').single();
  if (insErr) {
    console.error('❌ share-to-files INSERT:', insErr);
    // Cleanup orphaned upload — best-effort, don't mask the original error.
    try { await supa.storage.from('huddledin-files').remove([destPath]); } catch (_) {}
    throw insErr;
  }
  const fileId = insRow?.id;

  // Step 4 — Optimistic DB.folders push (mirror Sub-commit 12a pattern).
  // Local file row shape uses camelCase (see SB.getFolders mapping at index.html:1911).
  try {
    const _fls = [...(DB.folders || [])];
    const _fIdx = _fls.findIndex(f => f.key === folderKey && (
      (isPreParent && f.specialistPatientId === patientId) ||
      (!isPreParent && f.childId === patientId)
    ));
    if (_fIdx > -1) {
      const _newFileRow = {
        id: fileId,
        name: fname,
        date: window.HUD._localToday(),
        locked: false,
        sharedWith: [],
        uploadedBy: sess.id,
        storagePath: destPath,
        mimeType: 'application/pdf',
        specialistPatientId: isPreParent ? patientId : null,
        url: null,
      };
      _fls[_fIdx] = { ..._fls[_fIdx], files: [_newFileRow, ...((_fls[_fIdx].files) || [])] };
      hud.DB.folders = _fls;
      try { hud.re?.(); } catch (_) {}
    }
    // If folder not in local cache, skip optimistic push — realtime echo / next visit loads it.
  } catch (e) {
    console.error('❌ share-to-files optimistic push:', e);
    // Non-fatal — DB row exists, just local cache out of sync until next refresh.
  }

  return { fileId, folderKey, destPath, filename: fname };
}

// Sub-commit 10: full share-to-files flow for finalized reports (connected child).
// Calls _pdfToPatientFolder for Steps 1-4, then fires the parent notification (Step 5),
// then flips shared_with_parents=true LAST (Step 6 / commit point).
//
// Pre-parent reports take a DIFFERENT path — they don't call this function. See
// tiptap-gate.js _handleShare for the pre-parent branch which calls
// _pdfToPatientFolder directly + sets pending_share=true.
export async function _shareReportWithParents(reportRow, content, supa, sess, opts = {}) {
  if (!reportRow?.id) throw new Error('No report id');
  // Two-device race defense: idempotency guard.
  if (reportRow.shared_with_parents) return { ok: true, alreadyShared: true };

  const isPreParent = !reportRow.child_id && !!reportRow.specialist_patient_id;
  const patientId = isPreParent ? reportRow.specialist_patient_id : reportRow.child_id;
  const specId = sess?.specialistId || sess?.id;
  const folderKey = 'spec_' + (isPreParent ? patientId : specId);

  // Steps 1-4 via helper.
  const { fileId, folderKey: usedFolderKey } = await _pdfToPatientFolder({
    reportRow, content, supa, sess, isPreParent, patientId, folderKey, blob: opts.blob || undefined, // Q4: the stored bytes
  });

  // Step 5 — Notification (connected child only; pre-parent has no parent yet).
  if (!isPreParent) {
    try {
      const hud = H();
      const childName = (hud.DB?.children || []).find(c => c.id === patientId)?.name
        || (hud.LS?.get?.('children', []) || []).find(c => c.id === patientId)?.name
        || 'your child';
      const specName = sess?.displayName || sess?.name || 'Your specialist';
      if (typeof hud.notifyOtherParty === 'function') {
        await hud.notifyOtherParty(
          'report_share',
          T('notif_report_share', { spec: specName, child: childName }),
          patientId,
          'files',
          { fileId, folderKey: usedFolderKey, childId: patientId },
          null,
          null,
          hud._nTw?.('notif_report_share', { spec: specName, child: childName })   // NOTIF-HE-BODY: the {en,he} twin of the body
        );
      }
    } catch (e) {
      console.error('❌ share-to-files notification:', e);
      // Non-fatal — file is uploaded and the next step still commits the share.
    }
  }

  // Step 6 — UPDATE the reports row (the commit). If anything before fails, the
  // report stays in unshared state and the Share button remains visible for retry.
  const nowIso = new Date().toISOString();
  const { data: _shRows, error } = await supa.from('reports')
    .update({ shared_with_parents: true, shared_at: nowIso, updated_at: nowIso })
    .eq('id', reportRow.id)
    .select('id');
  if (error) throw error;
  if (!_shRows || !_shRows.length) throw new Error('0 rows'); // R3a: an RLS 0-row write is a failure, not a share
  return { ok: true, sharedAt: nowIso, fileId, folderKey: usedFolderKey };
}

function _newReportFromHub() {
  const { toast, openModal, el, mkBtn } = H();
  if (_capRefused()) return;
  if (typeof window.HUD_openTiptapGate !== 'function') { toast(T('rb_editor_not_loaded'), 'info'); return; }
  // Sub-commit 9a: merge connected children with active pre-parent patients (kind-tagged entries).
  const { DB, session } = H();
  const _isSpec = session?.role === 'specialist';
  const _children = getChildren().map(c => ({ kind: 'child', id: c.id, name: c.name || '', avatar: c.avatar || '\ud83e\uddd2' }));
  const _preParents = _isSpec
    ? (DB?.specialistPatients || []).filter(sp => sp.status === 'active').map(sp => ({ kind: 'spec_patient', id: sp.id, name: sp.name || '', avatar: sp.avatar_emoji || '\ud83e\uddd2' }))
    : [];
  const _all = [..._children, ..._preParents];
  if (!_all.length) { toast(T('rb_no_patients_connected'), 'info'); return; }
  if (_all.length === 1) {
    const only = _all[0];
    if (only.kind === 'spec_patient') window.HUD_openTiptapGate({ specialistPatientId: only.id, startNew: true });
    else window.HUD_openTiptapGate({ childId: only.id, startNew: true });
    return;
  }
  const sorted = _all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  openModal(T('spec_dt_select_patient'), (mb, close) => {
    const searchInp = el('input', {
      class: 'inp',
      placeholder: T('rb_search_patients_ph'),
      dir: 'auto',
      style: { marginBottom: '10px' },
    });
    const listEl = el('div', {});

    function _renderList() {
      listEl.innerHTML = '';
      const q = (searchInp.value || '').trim().toLowerCase();
      const filtered = q ? sorted.filter(c => (c.name || '').toLowerCase().includes(q)) : sorted;
      if (!filtered.length) {
        listEl.appendChild(el('div', {
          style: { padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '.84rem' },
        }, [T('rb_no_patients_match')]));
        return;
      }
      filtered.forEach(c => {
        const row = el('div', { class: 'rpt-card', style: { cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' } });
        row.appendChild(el('span', { style: { fontSize: '1.3rem' } }, [c.avatar || '\ud83e\uddd2']));
        const nameWrap = el('div', { style: { flex: '1', fontWeight: 700, color: '#0f172a' } });
        nameWrap.textContent = c.name;
        row.appendChild(nameWrap);
        if (c.kind === 'spec_patient') {
          row.appendChild(el('span', { class: 'rpt-badge', style: { background: '#fef3c7', color: '#92400e' } }, [T('patients_no_parent')]));
        }
        row.onclick = () => {
          close();
          if (c.kind === 'spec_patient') window.HUD_openTiptapGate({ specialistPatientId: c.id, startNew: true });
          else window.HUD_openTiptapGate({ childId: c.id, startNew: true });
        };
        listEl.appendChild(row);
      });
    }

    searchInp.oninput = _renderList;
    mb.appendChild(searchInp);
    withClear(searchInp); // F-107
    mb.appendChild(listEl);
    _renderList();
  }, 380);
}

// ════════════════════════════════════════
// REPORTS HUB
// ════════════════════════════════════════
export function renderReports() {
  injectStyles();
  const { el, mkBtn, toast, _hasSpecAiAccess, _showSpecAiUpgradeModal, S } = H();
  S.activeTab = 'reports';
  const sec = document.createElement('div'); sec.className = 'section';

  if (!RS.templatesLoaded || !RS.reportsLoaded) {
    sec.appendChild(el('div', { style: { textAlign: 'center', padding: '24px', color: '#64748b' } }, [T('btn_loading')]));
    loadData().then(() => H().re());
    return sec;
  }

  // First time — no reports yet
  if (!RS.templates.length && !RS.reports.length) {
    sec.appendChild(el('h2', { class: 'page-title' }, ['\ud83d\udccb ' + T('rb_page_title')]));
    const _b0 = _roBanner(); if (_b0) sec.appendChild(_b0);
    sec.appendChild(el('div', { class: 'empty-state', style: { marginTop: '8px' } }, [
      el('span', { class: 'empty-state-icon' }, ['\ud83d\udccb']),
      el('div', { class: 'empty-state-title' }, [T('rb_welcome_title')]),
      el('div', { class: 'empty-state-body' }, [T('rb_welcome_sub')]),
      el('div', { class: 'empty-state-actions', style: { display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' } }, [
        _paidBtn(mkBtn('+ ' + T('rb_new_report'), 'btn-md btn-primary', _paid(() => _newReportFromHub()))),
        _paidBtn(mkBtn('\ud83d\udce5 ' + T('rb_upload_template'), 'btn-md btn-secondary', _paid(() => { if (typeof window.HUD_UPLOAD_TEMPLATE === 'function') window.HUD_UPLOAD_TEMPLATE(); }))),
      ])
    ]));
    return sec;
  }

  // Hub with reports
  const hdr = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' } });
  hdr.appendChild(el('h2', { class: 'page-title', style: { margin: 0 } }, ['\ud83d\udccb ' + T('rb_page_title')]));
  // F-24: the counter + buttons wrap onto more lines instead of running off a phone screen
  const hdrR = el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px 10px', flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' } });
  if (_isCapped()) hdrR.appendChild(el('span', { class: 'rpt-counter' }, [T('rb_monthly_count', { n: RS.monthlyCount, max: MONTHLY_LIMIT })])); // F-110: the n / 5 counter is for capped (trial) users only
  hdrR.appendChild(mkBtn('\u2699\ufe0f', 'btn-sm btn-ghost', () => { const { S } = H(); S.activeTab = 'settings'; H().re(); }));
  hdrR.appendChild(mkBtn('\ud83d\udcdd ' + T('rb_my_templates'), 'btn-sm btn-ghost', () => nav('templates')));
  hdrR.appendChild(mkBtn('\ud83d\udcac ' + T('rb_my_phrases'), 'btn-sm btn-ghost', () => nav('phrases')));
  hdrR.appendChild(_paidBtn(mkBtn('+ ' + T('rb_new_report'), 'btn-sm btn-primary', _paid(() => _newReportFromHub()))));
  hdr.appendChild(hdrR); sec.appendChild(hdr);
  const _b1 = _roBanner(); if (_b1) sec.appendChild(_b1);

  // Drafts section
  const draftsHost = el('div', {});
  sec.appendChild(draftsHost);
  _renderDraftsSection(draftsHost);

  if (!RS.reports.length) {
    sec.appendChild(el('div', { style: { textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '.88rem' } }, [T('rb_no_reports_yet')]));
    return sec;
  }

  const children = getChildren();
  // SC4 — scope the reports list to the active clinic (child- or specialist_patient-derived).
  // Child-less/unresolvable → shown. monthlyCount (loadData) stays all-clinics per lock.
  RS.reports.filter(r => r.status !== 'draft' && (window.HUD?._reportInActiveClinic?.(r) ?? true)).forEach(r => {
    // Sub-commit 4f — resolve patient via context helper, supporting both connected children and pre-parent specialist_patients.
    const _patient = r.specialist_patient_id
      ? window.HUD?._resolveContextPatient?.({ kind: 'spec_patient', specialist_patient_id: r.specialist_patient_id })
      : children.find(c => c.id === r.child_id);
    const _isPreParent = !!r.specialist_patient_id;
    const child = _patient; // keep var name for downstream readability
    const card = el('div', { class: 'rpt-card' });
    const top = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' } });
    top.appendChild(el('div', { style: { fontWeight: 700, color: '#0f172a', fontSize: '.88rem' } }, [T('rb_patient_report_row', { avatar: child?.avatar || '\ud83e\uddd2', name: child?.name || T('rb_patient_fallback'), title: r.name || T('rb_untitled') })]));
    const badges = el('div', { style: { display: 'flex', gap: '4px' } });
    badges.appendChild(statusBadge(r.status));
    if (_isPreParent) badges.appendChild(el('span', { class: 'rpt-badge', style: { background: '#fef3c7', color: '#92400e' } }, [T('patients_no_parent')]));
    if (r.pending_share) badges.appendChild(el('span', { class: 'rpt-badge', style: { background: '#fef3c7', color: '#92400e' } }, ['⏳ ' + T('report_status_pending_share')]));
    else if (r.shared_with_parents) badges.appendChild(el('span', { class: 'rpt-badge', style: { background: '#dbeafe', color: '#1e40af' } }, ['📤 ' + T('rb_shared')]));
    top.appendChild(badges);
    card.appendChild(top);
    card.appendChild(el('div', { style: { fontSize: '.76rem', color: '#64748b' } }, [(r.created_at ? _locDate(new Date(r.created_at)) : '')]));
    card.onclick = () => {
      if (r.content && typeof window.HUD_openTiptapGate === 'function') {
        window.HUD_openTiptapGate({
          childId: r.child_id,
          specialistPatientId: r.specialist_patient_id,
          draftId: r.status === 'draft' ? r.id : undefined,
          reportId: r.status !== 'draft' ? r.id : undefined,
          readOnly: r.status === 'finalized' || r.status === 'final', // R3a: legacy 'final' = finished (read-only)
        });
      } else {
        toast(T('rb_legacy_report'), 'info');
      }
    };
    sec.appendChild(card);
  });
  return sec;
}

// ════════════════════════════════════════
// MY TEMPLATES
// ════════════════════════════════════════
export function renderTemplates() {
  injectStyles();
  const { el, mkBtn, toast, openConfirm } = H();
  const sec = document.createElement('div'); sec.className = 'section';

  const back = el('span', { style: { color: '#0d9488', cursor: 'pointer', fontWeight: 600, fontSize: '.84rem' } }, ['← ' + T('rb_back_to_reports')]);
  back.onclick = () => nav('hub'); sec.appendChild(back);

  const hdr = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '14px 0 16px' } });
  hdr.appendChild(el('h2', { style: { fontWeight: 800, color: '#0f172a', fontSize: '1.1rem', margin: 0 } }, [T('rb_my_templates')]));
  const hdrBtns = el('div', { style: { display: 'flex', gap: '8px' } });
  hdrBtns.appendChild(_paidBtn(mkBtn('\ud83d\udce5 ' + T('rb_upload_template'), 'btn-sm btn-ghost', _paid(() => {
    if (typeof window.HUD_UPLOAD_TEMPLATE === 'function') window.HUD_UPLOAD_TEMPLATE();
  }))));
  hdrBtns.appendChild(_paidBtn(mkBtn('+ ' + T('rb_new_template_btn'), 'btn-sm btn-primary', _paid(() => {
    if (typeof window.HUD_openTiptapGate === 'function') window.HUD_openTiptapGate({ templateMode: true });
  }))));
  hdr.appendChild(hdrBtns);
  sec.appendChild(hdr);
  const _b2 = _roBanner(); if (_b2) sec.appendChild(_b2);

  if (!RS.templates.length) {
    sec.appendChild(el('div', { style: { textAlign: 'center', padding: '20px', color: '#64748b' } }, [T('rb_no_templates_yet')]));
    return sec;
  }

  const grid = el('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '12px' } });
  RS.templates.forEach(t => {
    const card = el('div', { class: 'rpt-tpl-card' });
    const nameRow = el('div', { style: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' } });
    nameRow.appendChild(el('span', { style: { fontWeight: 700, color: '#0f172a' } }, [t.name]));
    if (t.name?.includes('(Draft)')) nameRow.appendChild(el('span', { class: 'rpt-badge rpt-badge-draft' }, [T('rb_template_badge_draft')]));
    if (t.source === 'imported') nameRow.appendChild(el('span', { class: 'rpt-badge', style: { background: '#ede9fe', color: '#6d28d9' } }, [T('rb_template_badge_imported')]));
    card.appendChild(nameRow);
    card.appendChild(el('div', { style: { fontSize: '.76rem', color: '#64748b', marginBottom: '8px' } }, [t.description || '']));
    const meta = el('div', { style: { fontSize: '.7rem', color: '#94a3b8' } });
    const secCount = _templateSectionCount(t);
    meta.textContent = T('rb_template_meta', { n: secCount, m: t.use_count || 0 });
    card.appendChild(meta);
    const acts = el('div', { style: { display: 'flex', gap: '8px', marginTop: '10px' } });
    const _copyTpl = () => ({ ...t, sections: JSON.parse(JSON.stringify(t.sections || [])), content: t.content ? JSON.parse(JSON.stringify(t.content)) : null });
    acts.appendChild(_paidBtn(mkBtn(T('rb_edit'), 'btn-sm btn-ghost', _paid((e) => {
      e.stopPropagation();
      if (t.content && typeof window.HUD_openTiptapGate === 'function') {
        window.HUD_openTiptapGate({ templateMode: true, templateId: t.id, templateContent: t.content, templateName: t.name, templateDescription: t.description, sourceFileName: t.name });
      } else {
        toast(T('rb_legacy_template'), 'info');
      }
    }))));
    acts.appendChild(_paidBtn(mkBtn(T('rb_duplicate'), 'btn-sm btn-ghost', _paid(async (e) => {
      e.stopPropagation();
      try {
        const clone = _copyTpl();
        delete clone.id;
        clone.name = T('rb_copy_suffix', { name: clone.name || 'Template' });
        clone.use_count = 0;
        const newId = await saveTemplate(clone);
        clone.id = newId;
        RS.currentTemplate = clone;
        RS.templatesLoaded = false;
        toast('\ud83d\udccb ' + T('rb_template_duplicated'));
        if (clone.content && typeof window.HUD_openTiptapGate === 'function') {
          await loadData();
          H().re();
          window.HUD_openTiptapGate({ templateMode: true, templateId: newId, templateContent: clone.content, templateName: clone.name, templateDescription: clone.description, sourceFileName: clone.name });
        } else {
          RS.templatesLoaded = false; await loadData(); H().re();
        }
      } catch (ex) { console.error(ex); toast(T('rb_could_not_duplicate'), 'error'); }
    }))));
    acts.appendChild(mkBtn(T('btn_delete'), 'btn-sm btn-ghost', (e) => {
      e.stopPropagation();
      openConfirm(T('rb_delete_template_title'), T('rb_cannot_undo'), true, async () => {
        const res = await deleteTemplate(t.id);
        if (!res?.ok) toast(T('rb_delete_template_fail'), 'error'); // R3a (D4): never a silent failure
        RS.templatesLoaded = false; await loadData(); H().re();
      });
    }));
    card.appendChild(acts);
    card.onclick = () => {
      if (t.content && typeof window.HUD_openTiptapGate === 'function') {
        window.HUD_openTiptapGate({ templateMode: true, templateId: t.id, templateContent: t.content, templateName: t.name, templateDescription: t.description, sourceFileName: t.name });
      } else {
        toast(T('rb_legacy_template'), 'info');
      }
    };
    grid.appendChild(card);
  });
  sec.appendChild(grid);
  return sec;
}

// ════════════════════════════════════════
// INIT + ROUTING
// ════════════════════════════════════════
export function initReports() {
  injectStyles();
  console.log('[Huddledin] Reports v2 module initialized');
}

// ════════════════════════════════════════
// BETA: Drafts section on Reports hub
// ════════════════════════════════════════
function _relativeTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffS = Math.floor((now - d) / 1000);
  if (diffS < 60) return T('rb_edited_just_now');
  if (diffS < 3600) return T('rb_edited_minutes_ago', { n: Math.floor(diffS / 60) });
  const time = _locTime(d, { hour: '2-digit', minute: '2-digit' }, []);
  if (d.toDateString() === now.toDateString()) return T('rb_edited_today_at', { time });
  const yest = new Date(now); yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return T('rb_edited_yesterday_at', { time });
  const diffD = Math.floor(diffS / 86400);
  if (diffD <= 7) return T('rb_edited_days_ago', { n: diffD });
  return T('rb_edited_on', { date: _locDate(d) });
}

function _draftPreview(content) {
  try {
    const sections = content?.content;
    if (!sections?.length) return T('rb_empty_draft');
    const sec = sections[0];
    const titleNode = sec.content?.[0];
    const bodyNode = sec.content?.[1];
    const titleText = titleNode?.content?.map(n => n.text || '').join('').trim() || '';
    let bodyText = '';
    if (bodyNode?.content) {
      for (const block of bodyNode.content) {
        if (block.content) bodyText += block.content.map(n => n.text || '').join('');
        if (bodyText.length > 60) break;
      }
    }
    bodyText = bodyText.trim();
    if (bodyText.length > 60) bodyText = bodyText.slice(0, 60) + '\u2026';
    if (titleText && bodyText) return titleText + ' \u2014 ' + bodyText;
    if (titleText) return T('rb_draft_no_content', { title: titleText });
    if (bodyText) return bodyText;
    return T('rb_empty_draft');
  } catch (_) { return T('rb_empty_draft'); }
}

async function _renderDraftsSection(host) {
  const { el, mkBtn, toast, session, openConfirm } = H();
  const { listDrafts, deleteDraft } = await window.HUD_TIPTAP_API();
  const _allDrafts = await listDrafts({ specialistId: session.id });
  // SC4 — scope drafts to the active clinic (child- or specialist_patient-derived; unresolvable → Private-only).
  const drafts = _allDrafts.filter(d => window.HUD?._reportInActiveClinic?.(d) ?? true);
  const ghosts = (RS.ghosts || []).filter(g => window.HUD?._reportInActiveClinic?.(g) ?? true); // R3f
  if (!drafts.length && !ghosts.length) return;

  const children = getChildren();
  const wrap = el('div', { style: { marginBottom: '20px' } });
  wrap.appendChild(el('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' } }, [
    el('span', { style: { fontWeight: 700, fontSize: '.92rem', color: '#0f172a' } }, [T('rb_drafts_header')]),
    el('span', { style: { fontSize: '.72rem', fontWeight: 600, color: '#64748b', background: '#f0fdf9', borderRadius: '99px', padding: '2px 8px' } }, [String(drafts.length + ghosts.length)]),
  ]));

  drafts.forEach(draft => {
    // F-45c: a private client's draft resolves like the finalized rows (child-shaped object via the context helper)
    const child = draft.specialist_patient_id
      ? window.HUD?._resolveContextPatient?.({ kind: 'spec_patient', specialist_patient_id: draft.specialist_patient_id })
      : children.find(c => c.id === draft.child_id);
    const card = el('div', { class: 'rpt-card', style: { position: 'relative', paddingInlineEnd: '42px' } });
    card.appendChild(el('div', { style: { fontWeight: 600, color: '#0f172a', fontSize: '.88rem', marginBottom: '2px' } }, [T('rb_patient_report_row', { avatar: child?.avatar || '\ud83e\uddd2', name: child?.name || T('rb_patient_fallback'), title: draft.name || T('rb_untitled') })]));
    card.appendChild(el('div', { style: { fontSize: '.72rem', color: '#94a3b8' } }, [_relativeTime(draft.updated_at)]));

    const trash = el('button', { style: { position: 'absolute', top: '50%', insetInlineEnd: '10px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '6px', borderRadius: '6px', color: '#94a3b8', transition: 'color .12s, background .12s', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, ['🗑️']);
    trash.onmouseenter = () => { trash.style.color = '#dc2626'; trash.style.background = '#fef2f2'; };
    trash.onmouseleave = () => { trash.style.color = '#94a3b8'; trash.style.background = 'none'; };
    trash.onclick = (e) => {
      e.stopPropagation();
      openConfirm(T('rb_delete_draft_title'), T('rb_cannot_undo'), true, async () => {
        const result = await deleteDraft({ reportId: draft.id });
        if (result.ok) { RS.reportsLoaded = false; H().re(); }
        else toast(T('rb_delete_draft_fail'), 'error');
      });
    };
    card.appendChild(trash);

    card.onclick = () => {
      if (typeof window.HUD_openTiptapGate === 'function') window.HUD_openTiptapGate({ draftId: draft.id, childId: draft.child_id, specialistPatientId: draft.specialist_patient_id || null });
    };
    wrap.appendChild(card);
  });
  // R3f: placeholder rows for device copies of reports finalized / deleted on another device
  ghosts.forEach(g => {
    const _p = g.specialist_patient_id
      ? window.HUD?._resolveContextPatient?.({ kind: 'spec_patient', specialist_patient_id: g.specialist_patient_id })
      : children.find(c => c.id === g.child_id);
    wrap.appendChild(_ghostCard(g, T('rb_patient_report_row', { avatar: _p?.avatar || _p?.avatar_emoji || '🧒', name: _p?.name || T('rb_patient_fallback'), title: g.name || T('rb_untitled') })));
  });
  host.appendChild(wrap);
}

// ════════════════════════════════════════
// BETA: New Tiptap editor from patient context
// ════════════════════════════════════════
async function handleNewReport(arg) {
  // Sub-commit 4f — accept {childId, specialistPatientId} object OR legacy bare childId.
  const childId = typeof arg === 'string' ? arg : (arg?.childId || null);
  const specialistPatientId = typeof arg === 'object' ? (arg?.specialistPatientId || null) : null;
  const { session, openModal, el, mkBtn, toast } = H();
  if (!session?.id || (!childId && !specialistPatientId)) return;
  if (_capRefused()) return; // F-110: the same cap at the function every caller reaches
  if (typeof window.HUD_openTiptapGate !== 'function') { toast(T('rb_editor_not_loaded'), 'info'); return; }

  const { findExistingDraft } = await window.HUD_TIPTAP_API();
  const existing = await findExistingDraft({ specialistId: session.id, childId, specialistPatientId });

  if (!existing) {
    window.HUD_openTiptapGate({ childId, specialistPatientId });
    return;
  }

  const d = new Date(existing.updated_at);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = _locTime(d, { hour: '2-digit', minute: '2-digit' }, []);
  const dateStr = isToday ? T('rb_today_at', { time }) : isYesterday ? T('rb_yesterday_at', { time }) : T('rb_date_at', { date: _locDate(d), time });

  openModal(T('rb_continue_draft_title'), (mb, close) => {
    mb.appendChild(el('div', { style: { marginBottom: '20px', color: '#334155', fontSize: '14px', lineHeight: '1.5' } },
      [T('rb_resume_draft_new_body', { when: dateStr })]));
    const row = el('div', {}, []);
    row.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;';
    row.appendChild(mkBtn(T('btn_cancel'), 'btn-md btn-ghost', close));
    row.appendChild(mkBtn(T('rb_start_new'), 'btn-md btn-secondary', () => { close(); window.HUD_openTiptapGate({ childId, specialistPatientId, startNew: true }); }));
    row.appendChild(mkBtn(T('rb_resume_draft'), 'btn-md btn-primary', () => { close(); window.HUD_openTiptapGate({ draftId: existing.id, childId, specialistPatientId }); }));
    mb.appendChild(row);
  }, 420);
}

// ════════════════════════════════════════
// Template picker (shared by "From template" + "Use template" toolbar)
// ════════════════════════════════════════
async function _openTemplatePickerForChild({ childId, specialistPatientId, onPicked, reportDir }) {
  const { session, openModal, el, mkBtn, toast, _supa, openConfirm } = H();
  if (!session?.id) return;
  const { substitutePlaceholders } = await window.HUD_TIPTAP_API();

  let templates;
  try {
    const { data, error } = await _supa.from('report_templates')
      .select('id,name,description,content,updated_at,use_count')
      .eq('specialist_id', session.id)
      .not('content', 'is', null)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    templates = data || [];
  } catch (e) {
    console.error('\u274c load templates:', e);
    toast(T('rb_load_templates_fail'), 'error');
    return;
  }

  if (!templates.length) {
    openConfirm(T('rb_no_templates_yet'), T('rb_no_templates_long'), false, () => {});
    return;
  }

  // Sub-commit 4f — resolve patient via context helper to support pre-parent.
  const child = childId
    ? getChildren().find(c => c.id === childId)
    : (specialistPatientId
      ? window.HUD?._resolveContextPatient?.({ kind: 'spec_patient', specialist_patient_id: specialistPatientId })
      : null);

  openModal(T('rb_pick_template'), (mb, close) => {
    templates.forEach(t => {
      const row = el('div', { class: 'rpt-card', style: { cursor: 'pointer' } });
      row.appendChild(el('div', { style: { fontWeight: 700, color: '#0f172a', fontSize: '.88rem', marginBottom: '2px' } }, [t.name]));
      if (t.description) row.appendChild(el('div', { style: { fontSize: '.76rem', color: '#64748b', marginBottom: '4px' } }, [t.description]));
      const meta = [];
      const sc = t.content?.content?.length;
      if (sc) meta.push(T('rb_section_count', { n: sc }));
      if (t.use_count) meta.push(T('rb_used_times', { n: t.use_count }));
      if (meta.length) row.appendChild(el('div', { style: { fontSize: '.7rem', color: '#94a3b8' } }, [meta.join(' \u00b7 ')]));
      row.onclick = () => {
        close();
        const subbed = child ? substitutePlaceholders(t.content, child, { dir: reportDir }) : t.content; // HEB-TEMPLATE V5: reportDir is set only by "Use template" inside a report
        onPicked(subbed, t);
        _supa.from('report_templates').update({ use_count: (t.use_count || 0) + 1 }).eq('id', t.id).then(() => {}).catch(() => {});
      };
      mb.appendChild(row);
    });
  }, 480);
}

async function handleStartFromTemplate(arg) {
  // Sub-commit 4f \u2014 accept {childId, specialistPatientId} object OR legacy bare childId.
  const childId = typeof arg === 'string' ? arg : (arg?.childId || null);
  const specialistPatientId = typeof arg === 'object' ? (arg?.specialistPatientId || null) : null;
  const { session, openModal, el, mkBtn, toast } = H();
  if (!session?.id || (!childId && !specialistPatientId)) return;
  if (_capRefused()) return; // F-110: closes the template hole (a capped user could start a report from a template, which never checked the cap)
  if (typeof window.HUD_openTiptapGate !== 'function') { toast(T('rb_editor_not_loaded'), 'info'); return; }

  const { findExistingDraft } = await window.HUD_TIPTAP_API();
  const existing = await findExistingDraft({ specialistId: session.id, childId, specialistPatientId });

  if (existing) {
    const proceed = await new Promise(resolve => {
      const d = new Date(existing.updated_at);
      const now = new Date();
      const isToday = d.toDateString() === now.toDateString();
      const yest = new Date(now); yest.setDate(yest.getDate() - 1);
      const isYest = d.toDateString() === yest.toDateString();
      const time = _locTime(d, { hour: '2-digit', minute: '2-digit' }, []);
      const dateStr = isToday ? T('rb_today_at', { time }) : isYest ? T('rb_yesterday_at', { time }) : T('rb_date_at', { date: _locDate(d), time });
      openModal(T('rb_continue_draft_title'), (mb, close) => {
        mb.appendChild(el('div', { style: { marginBottom: '20px', color: '#334155', fontSize: '14px', lineHeight: '1.5' } },
          [T('rb_resume_draft_tpl_body', { when: dateStr })]));
        const row = el('div', {}); row.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;';
        row.appendChild(mkBtn(T('btn_cancel'), 'btn-md btn-ghost', () => { close(); resolve('cancel'); }));
        row.appendChild(mkBtn(T('rb_from_template'), 'btn-md btn-secondary', () => { close(); resolve('new'); }));
        row.appendChild(mkBtn(T('rb_resume_draft'), 'btn-md btn-primary', () => { close(); resolve('resume'); }));
        mb.appendChild(row);
      }, 420);
    });
    if (proceed === 'cancel') return;
    if (proceed === 'resume') { window.HUD_openTiptapGate({ draftId: existing.id, childId, specialistPatientId }); return; }
  }

  await _openTemplatePickerForChild({
    childId,
    specialistPatientId,
    onPicked: (subbed, t) => {
      window.HUD_openTiptapGate({ childId, specialistPatientId, initialContent: subbed, fromTemplateId: t.id, startNew: true });
    },
  });
}

// ════════════════════════════════════════
// PATIENT REPORTS TAB (filtered by child)
// ════════════════════════════════════════
function renderPatientReports() {
  injectStyles();
  const { el, mkBtn, toast, session, _supa, _hasSpecAiAccess, _showSpecAiUpgradeModal, S } = H();
  const sec = document.createElement('div'); sec.className = 'section';
  // Sub-commit 4f — context-aware resolution. Supports both connected-child
  // (S.activeChild) and pre-parent (S.activeSpecPatient) callers.
  const _ctx = window.HUD?._currentPatientContext?.();
  const childId = _ctx?.kind === 'child' ? _ctx.child_id : null;
  const specialistPatientId = _ctx?.kind === 'spec_patient' ? _ctx.specialist_patient_id : null;
  const _isPreParent = !!specialistPatientId;

  if (!RS.reportsLoaded || !RS.templatesLoaded) {
    sec.appendChild(el('div', { style: { textAlign: 'center', padding: '24px', color: '#64748b' } }, [T('btn_loading')]));
    loadData().then(() => H().re());
    return sec;
  }

  const children = getChildren();
  const child = _isPreParent
    ? window.HUD?._resolveContextPatient?.(_ctx)
    : children.find(c => c.id === childId);
  const childReports = _isPreParent
    ? RS.reports.filter(r => r.specialist_patient_id === specialistPatientId)
    : RS.reports.filter(r => r.child_id === childId);

  // Header
  const hdr = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' } });   // F-24
  hdr.appendChild(el('h2', { style: { fontWeight: 800, color: '#0f172a', fontSize: '1rem', margin: 0 } }, ['📋 ' + T('rb_page_title')]));
  const hdrBtns = el('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' } });   // F-24
  hdrBtns.appendChild(_paidBtn(mkBtn('\ud83d\udccb ' + T('rb_from_template'), 'btn-sm btn-ghost', _paid(() => handleStartFromTemplate({ childId, specialistPatientId })))));
  hdrBtns.appendChild(_paidBtn(mkBtn('+ ' + T('rb_new_report'), 'btn-sm btn-primary', _paid(() => {
    if (_capRefused()) return;
    handleNewReport({ childId, specialistPatientId });
  }))));
  hdr.appendChild(hdrBtns);
  sec.appendChild(hdr);
  const _b3 = _roBanner(); if (_b3) sec.appendChild(_b3);

  // R3f: this patient's placeholder rows (device copies of reports finalized / deleted on another device)
  const childGhosts = (RS.ghosts || []).filter(g => _isPreParent ? g.specialist_patient_id === specialistPatientId : g.child_id === childId);

  // Empty state
  if (!childReports.length && !childGhosts.length) {
    sec.appendChild(el('div', { class: 'empty-state', style: { marginTop: '8px' } }, [
      el('span', { class: 'empty-state-icon' }, ['📋']),
      el('div', { class: 'empty-state-title' }, [T('rb_no_reports_title')]),
      el('div', { class: 'empty-state-body' }, [T('rb_create_first_report', { name: child?.name || T('rb_patient_fallback') })])
    ]));
    return sec;
  }

  // Report list
  childReports.forEach(r => {
    const card = el('div', { class: 'rpt-card', style: Object.assign({ position: 'relative' }, r.status === 'draft' ? { paddingInlineEnd: '42px' } : {}) });
    const top = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' } });
    top.appendChild(el('div', { style: { fontWeight: 700, color: '#0f172a', fontSize: '.88rem' } }, [r.name || T('rb_untitled')]));
    const badges = el('div', { style: { display: 'flex', gap: '4px' } });
    badges.appendChild(statusBadge(r.status));
    if (r.pending_share) badges.appendChild(el('span', { class: 'rpt-badge', style: { background: '#fef3c7', color: '#92400e' } }, ['\u23f3 ' + T('report_status_pending_share')]));
    else if (r.shared_with_parents) badges.appendChild(el('span', { class: 'rpt-badge', style: { background: '#dbeafe', color: '#1e40af' } }, ['\ud83d\udce4 ' + T('rb_shared')]));
    top.appendChild(badges);
    card.appendChild(top);
    card.appendChild(el('div', { style: { fontSize: '.76rem', color: '#64748b' } }, [r.created_at ? _locDate(new Date(r.created_at)) : '']));
    if (r.status === 'draft') {
      const trash = el('button', { style: { position: 'absolute', top: '50%', insetInlineEnd: '10px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '6px', borderRadius: '6px', color: '#94a3b8', transition: 'color .12s, background .12s', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, ['\ud83d\uddd1\ufe0f']);
      trash.onmouseenter = () => { trash.style.color = '#dc2626'; trash.style.background = '#fef2f2'; };
      trash.onmouseleave = () => { trash.style.color = '#94a3b8'; trash.style.background = 'none'; };
      trash.onclick = (e) => {
        e.stopPropagation();
        const { openConfirm } = H();
        openConfirm(T('rb_delete_draft_title2'), T('rb_cannot_undo'), true, async () => {
          const { deleteDraft } = await window.HUD_TIPTAP_API();
          const result = await deleteDraft({ reportId: r.id });
          if (result.ok) { RS.reportsLoaded = false; H().re(); }
          else toast(T('rb_delete_draft_fail'), 'error');
        });
      };
      card.appendChild(trash);
    }
    card.onclick = () => {
      if (r.content && typeof window.HUD_openTiptapGate === 'function') {
        window.HUD_openTiptapGate({
          childId: r.child_id,
          specialistPatientId: r.specialist_patient_id,
          draftId: r.status === 'draft' ? r.id : undefined,
          reportId: r.status !== 'draft' ? r.id : undefined,
          readOnly: r.status === 'finalized' || r.status === 'final', // R3a: legacy 'final' = finished (read-only)
        });
      } else {
        toast(T('rb_legacy_report'), 'info');
      }
    };
    sec.appendChild(card);
  });
  childGhosts.forEach(g => sec.appendChild(_ghostCard(g, g.name || T('rb_untitled')))); // R3f

  return sec;
}

// ════════════════════════════════════════
// MY PHRASES
// ════════════════════════════════════════
function _relTimeShort(ts) {
  if (!ts) return '';
  const d = new Date(ts), now = new Date(), s = Math.floor((now - d) / 1000);
  if (s < 60) return T('rb_just_now');
  if (s < 3600) return T('rb_rel_min_ago', { n: Math.floor(s / 60) });
  if (d.toDateString() === now.toDateString()) return T('cal_today');
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return T('rb_yesterday');
  return _locDate(d);
}

function _openPhraseDialog({ phraseId, initialName, initialContent, onSave, onDelete }) {
  const { openModal, el, mkBtn, toast, openConfirm } = H();
  const isEdit = !!phraseId;
  openModal(isEdit ? T('rb_edit_phrase') : T('rb_new_phrase'), (mb, close) => {
    const nameLabel = document.createElement('label');
    nameLabel.style.cssText = 'display:block;font-size:13px;font-weight:600;color:#334155;margin-bottom:4px;';
    nameLabel.textContent = T('rb_name_label') + ' *';
    mb.appendChild(nameLabel);
    const nameInp = document.createElement('input');
    nameInp.type = 'text';
    nameInp.maxLength = 100;
    nameInp.value = initialName || '';
    nameInp.placeholder = T('rb_phrase_name_ph');
    nameInp.style.cssText = 'width:100%;padding:10px 12px;border:1.5px solid #d1e0dd;border-radius:8px;font-size:15px;font-family:inherit;box-sizing:border-box;margin-bottom:12px;outline:none;';
    nameInp.onfocus = () => { nameInp.style.borderColor = '#0d9488'; };
    nameInp.onblur = () => { nameInp.style.borderColor = '#d1e0dd'; };
    mb.appendChild(nameInp);

    const contentLabel = document.createElement('label');
    contentLabel.style.cssText = 'display:block;font-size:13px;font-weight:600;color:#334155;margin-bottom:4px;';
    contentLabel.textContent = T('rb_content_label') + ' *';
    mb.appendChild(contentLabel);
    const contentInp = document.createElement('textarea');
    contentInp.rows = 5;
    contentInp.maxLength = 5000;
    contentInp.value = initialContent || '';
    contentInp.placeholder = T('rb_phrase_content_ph');
    contentInp.style.cssText = 'width:100%;padding:10px 12px;border:1.5px solid #d1e0dd;border-radius:8px;font-size:15px;font-family:inherit;box-sizing:border-box;resize:vertical;margin-bottom:4px;outline:none;';
    mb.appendChild(contentInp);

    mb.appendChild(el('div', { style: { fontSize: '12px', color: '#94a3b8', marginBottom: '12px', lineHeight: '1.4' } },
      [T('rb_phrase_placeholder_help')]));

    const errEl = document.createElement('div');
    errEl.style.cssText = 'color:#ef4444;font-size:13px;margin-bottom:8px;display:none;';
    mb.appendChild(errEl);

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;';

    if (isEdit && onDelete) {
      const delBtn = document.createElement('button');
      delBtn.className = 'btn-md btn-ghost';
      delBtn.textContent = T('btn_delete');
      delBtn.style.cssText += 'color:#ef4444;margin-inline-end:auto;';
      delBtn.onclick = () => {
        openConfirm(T('rb_delete_phrase_title'), T('rb_phrase_remove_perm'), true, async () => {
          const r = await onDelete();
          if (r?.error) { toast(r.error, 'error'); return; }
          close();
        });
      };
      row.appendChild(delBtn);
    }

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn-md btn-ghost';
    cancelBtn.textContent = T('btn_cancel');
    cancelBtn.onclick = close;
    row.appendChild(cancelBtn);

    const saveBtn = document.createElement('button');
    saveBtn.className = 'btn-md btn-primary';
    saveBtn.textContent = isEdit ? T('rb_save_changes') : T('rb_create');
    saveBtn.onclick = async () => {
      const n = nameInp.value.trim();
      const c = contentInp.value.trim();
      if (!n) { errEl.textContent = T('rb_name_required'); errEl.style.display = 'block'; nameInp.focus(); return; }
      if (!c) { errEl.textContent = T('rb_content_required'); errEl.style.display = 'block'; contentInp.focus(); return; }
      if (n.length > 100) { errEl.textContent = T('rb_name_max_100'); errEl.style.display = 'block'; return; }
      if (c.length > 5000) { errEl.textContent = T('rb_content_max_5000'); errEl.style.display = 'block'; return; }
      saveBtn.disabled = true; saveBtn.textContent = T('rb_saving');
      const result = await onSave(n, c);
      if (result?.error) { errEl.textContent = result.error; errEl.style.display = 'block'; saveBtn.disabled = false; saveBtn.textContent = isEdit ? T('rb_save_changes') : T('rb_create'); return; }
      close();
    };
    row.appendChild(saveBtn);
    // F-19: read-only without a plan — the phrase is shown, Save pauses (upgrade screen); Delete stays (QP-2).
    if (H()._specAiReadOnly?.()) { nameInp.readOnly = true; contentInp.readOnly = true; _paidBtn(saveBtn); saveBtn.onclick = _paid(saveBtn.onclick); }
    mb.appendChild(row);
    setTimeout(() => nameInp.focus(), 50);
  }, 480);
}

function renderPhrases() {
  injectStyles();
  const { el, mkBtn, toast } = H();
  const sec = document.createElement('div'); sec.className = 'section';

  const back = el('span', { style: { color: '#0d9488', cursor: 'pointer', fontWeight: 600, fontSize: '.84rem' } }, ['\u2190 ' + T('rb_back_to_reports')]);
  back.onclick = () => nav('hub'); sec.appendChild(back);

  const hdr = el('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '14px 0 16px' } });
  hdr.appendChild(el('h2', { style: { fontWeight: 800, color: '#0f172a', fontSize: '1.1rem', margin: 0 } }, ['\ud83d\udcac ' + T('rb_my_phrases')]));
  hdr.appendChild(_paidBtn(mkBtn('+ ' + T('rb_new_phrase_btn'), 'btn-sm btn-primary', _paid(() => {
    _openPhraseDialog({
      onSave: async (name, content) => {
        const result = await savePhrase({ phraseId: null, name, content });
        if (result.error) return result;
        RS.phrasesLoaded = false; await loadData(); H().re();
        toast('\u2705 ' + T('rb_phrase_created'));
        return {};
      },
    });
  }))));
  sec.appendChild(hdr);
  const _b4 = _roBanner(); if (_b4) sec.appendChild(_b4);

  if (!RS.phrases.length) {
    sec.appendChild(el('div', { class: 'empty-state', style: { marginTop: '8px' } }, [
      el('span', { class: 'empty-state-icon' }, ['\ud83d\udcac']),
      el('div', { class: 'empty-state-title' }, [T('rb_no_phrases_long')]),
      el('div', { class: 'empty-state-body' }, [T('rb_phrases_help_body')]),
    ]));
    return sec;
  }

  const grid = el('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '12px' } });
  RS.phrases.forEach(p => {
    const card = el('div', { class: 'rpt-tpl-card', style: { cursor: 'pointer' } });
    card.appendChild(el('div', { style: { fontWeight: 700, color: '#0f172a', marginBottom: '4px' } }, [p.name]));
    const preview = (p.content || '').length > 100 ? p.content.slice(0, 100) + '\u2026' : p.content || '';
    card.appendChild(el('div', { style: { fontSize: '.78rem', color: '#64748b', marginBottom: '8px', lineHeight: '1.4' } }, [preview]));
    const meta = [];
    meta.push(T('rb_used_times', { n: p.use_count || 0 }));
    meta.push(_relTimeShort(p.last_used_at || p.updated_at || p.created_at));
    card.appendChild(el('div', { style: { fontSize: '.7rem', color: '#94a3b8' } }, [meta.filter(Boolean).join(' \u00b7 ')]));
    card.onclick = () => {
      _openPhraseDialog({
        phraseId: p.id,
        initialName: p.name,
        initialContent: p.content,
        onSave: async (name, content) => {
          const result = await savePhrase({ phraseId: p.id, name, content });
          if (result.error) return result;
          RS.phrasesLoaded = false; await loadData(); H().re();
          toast('\u2705 ' + T('rb_phrase_updated'));
          return {};
        },
        onDelete: async () => {
          const result = await deletePhrase(p.id);
          if (result.error) return result;
          RS.phrasesLoaded = false; await loadData(); H().re();
          toast(T('rb_phrase_deleted'));
          return {};
        },
      });
    };
    grid.appendChild(card);
  });
  sec.appendChild(grid);
  return sec;
}

// Main render dispatcher (called from index.html glue)
function renderMain() {
  switch (RS.view) {
    case 'templates': return renderTemplates();
    case 'phrases': return renderPhrases();
    default: return renderReports();
  }
}

function invalidateReportsCache() { RS.reportsLoaded = false; }

// ── Reports R3f — placeholder rows for device copies that can't be saved any more (notes SC4 / decision 19 parity) ──
// A device copy of a report that was DELETED on another device (no row) or FINALIZED there (status ≠ draft) is kept by
// the sweep, and listed here as a placeholder row: opening it shows the copy under the existing banner with Copy only;
// the copy is deleted only after Copy or when the user dismisses the row (🗑 + the existing delete-draft confirm).
// A never-created report (isNew) is not a placeholder — the sweep creates it when online.
RS.ghosts = [];
async function _loadGhosts() {
  RS.ghosts = [];
  try {
    const { session } = H();
    if (!session?.id) return;
    const ents = await rbAll(session.id);
    const byId = new Map(RS.reports.map(r => [r.id, r]));
    for (const e of ents) {
      if (e.isNew || !e.content) continue;
      const row = byId.get(e.reportId);
      const kind = !row ? 'deleted' : row.status !== 'draft' ? 'finalized' : null;
      if (kind) RS.ghosts.push({ id: e.reportId, child_id: e.childId || null, specialist_patient_id: e.specialistPatientId || null,
        name: e.name || null, updated_at: e.updatedAt, kind, _ghost: true });
    }
  } catch (e) { console.error('❌ report placeholder rows:', e); }
}
function dropGhost(id) { RS.ghosts = (RS.ghosts || []).filter(g => g.id !== id); }
// One placeholder card (hub drafts section / the patient's reports tab). `label` = the row title.
function _ghostCard(g, label) {
  const { el, openConfirm, session } = H();
  const card = el('div', { class: 'rpt-card rpt-ghost', style: { position: 'relative', paddingInlineEnd: '42px', cursor: 'pointer' } });
  card.appendChild(el('div', { style: { fontWeight: 600, color: '#0f172a', fontSize: '.88rem', marginBottom: '2px' } }, [label]));
  card.appendChild(el('span', { class: 'rpt-badge', style: { background: '#fef2f2', color: '#b91c1c' } }, [g.kind === 'deleted' ? T('notes_deleted_title') : T('rb_finalized_elsewhere')]));
  const trash = el('button', { style: { position: 'absolute', top: '50%', insetInlineEnd: '10px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '6px', borderRadius: '6px', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, ['🗑️']);
  trash.className = 'rpt-ghost-dismiss';
  trash.onclick = (ev) => {
    ev.stopPropagation();
    openConfirm(T('rb_delete_draft_title2'), T('rb_cannot_undo'), true, async () => {
      await rbDel(session.id, g.id); dropGhost(g.id); H().re();
    });
  };
  card.appendChild(trash);
  card.onclick = () => {
    if (typeof window.HUD_openTiptapGate === 'function') window.HUD_openTiptapGate({ draftId: g.id, childId: g.child_id, specialistPatientId: g.specialist_patient_id, ghost: g.kind });
  };
  return card;
}

// ── Reports R3e — the device-copy sweep (decision 19 / ruling D7: every signed-in specialist) ──
// At app start and on every `online`: this user's device copies of report drafts that aren't open. Nobody saved since
// → saved now (conditional update, or the INSERT of a never-created report with its device-made id), the copy
// deleted, ONE toast. Moved on / finalized / deleted on the server → kept (the open report shows the banner).
// Offline / unreachable → left as they are. Runs in app.bundle.js, so it never waits for the editor bundle.
const _canonJ = v => Array.isArray(v) ? v.map(_canonJ) : (v && typeof v === 'object') ? Object.keys(v).sort().reduce((o, k) => (o[k] = _canonJ(v[k]), o), {}) : v;
const _sameJ = (a, b) => JSON.stringify(_canonJ(a ?? null)) === JSON.stringify(_canonJ(b ?? null));
// QS1/QS2: how many report drafts have edits that haven't reached the server yet — a device copy (hud_report_drafts),
// or the open draft editor's edit still unsaved / saving. Sign-out wipes the device copies (privacy rule), so it asks first.
async function reportsUnsentCount() {
  const { session, S } = H();
  if (!session?.id) return 0;
  const ids = new Set();
  try { (await rbAll(session.id)).forEach(e => ids.add(e.reportId)); } catch (e) { console.error('❌ unsent reports check:', e); }
  const g = S?._activeGate;
  if (g && g._gateReportId && g._gateIsDirty && g._gateIsDirty()) ids.add(g._gateReportId()); // draft editor only (templates have no id hook)
  return ids.size;
}
let _rcInstalled = false, _rcBusy = false;
async function reportsRecover() {
  if (!_rcInstalled) { _rcInstalled = true; window.addEventListener('online', () => { reportsRecover(); }); }
  if (_rcBusy) return;
  _rcBusy = true;
  try {
    const { session, _supa, toast, S } = H();
    if (!session?.id || !_supa || navigator.onLine === false) return;
    // F-19 (planning chat): without plan access a report draft's device copy is NEVER saved, cleared or overwritten —
    // it waits, untouched, until the plan is renewed; the editor then offers it exactly as today (R3e).
    if (H()._specAiReadOnly?.()) return;
    const ents = await rbAll(session.id);
    if (!ents.length) return;
    const openId = S?._activeGate?._gateReportId?.() || null;
    let saved = 0;
    for (const ent of ents) {
      if (ent.reportId === openId) continue;                       // the open editor owns its copy
      if (!ent.content || (!ent.childId && !ent.specialistPatientId)) continue;
      const ts = new Date().toISOString();
      if (ent.isNew) {
        const fk = ent.specialistPatientId ? { specialist_patient_id: ent.specialistPatientId, child_id: null } : { child_id: ent.childId };
        const { data, error } = await _supa.from('reports')
          .insert({ id: ent.reportId, specialist_id: session.id, ...fk, content: ent.content, schema_version: 1, status: 'draft',
            report_type: 'general', name: ent.name || null, ...(ent.fields || {}), updated_at: ts, ...(ent.templateId ? { template_id: ent.templateId } : {}) })
          .select('id');
        if (!error && data && data.length === 1) { await rbDel(session.id, ent.reportId); saved++; continue; }
        if (error && error.code !== '23505') { console.error('❌ recover new report:', error); continue; }
        // 23505: the row already exists (an earlier lost reply) → handled like a saved report below
      }
      const { data: rows, error: rErr } = await _supa.from('reports').select('id, status, updated_at, content').eq('id', ent.reportId);
      if (rErr) { console.error('❌ recover report (read):', rErr); continue; }
      const row = rows && rows[0];
      if (!row || row.status !== 'draft') continue;                                       // deleted / finalized elsewhere → kept
      if (_sameJ(row.content, ent.content)) { await rbDel(session.id, ent.reportId); continue; } // already on the server
      if (!ent.base || Date.parse(ent.base) !== Date.parse(row.updated_at)) continue;     // moved on → kept (banner when opened)
      const { data: upd, error: uErr } = await _supa.from('reports')
        .update({ content: ent.content, schema_version: 1, name: ent.name || null, ...(ent.fields || {}), updated_at: ts })
        .eq('id', ent.reportId).eq('status', 'draft').eq('updated_at', row.updated_at)
        .select('id');
      if (uErr) { console.error('❌ recover report:', uErr); continue; }
      if (upd && upd.length === 1) { await rbDel(session.id, ent.reportId); saved++; }
    }
    if (saved) { invalidateReportsCache(); try { toast(T('notes_recovered_saved')); } catch (_) {} }
  } catch (e) { console.error('❌ recover report drafts:', e); }
  finally { _rcBusy = false; }
}
function invalidateTemplatesCache() { RS.templatesLoaded = false; }
function invalidatePhrasesCache() { RS.phrasesLoaded = false; }
function navToTemplates() {
  const { S } = H();
  S.activeTab = 'reports';
  nav('templates');
}

function openNewPhraseDialog(initialName, initialContent) {
  _openPhraseDialog({
    initialName: initialName || '',
    initialContent: initialContent || '',
    onSave: async (name, content) => {
      const result = await savePhrase({ phraseId: null, name, content });
      if (result.error) return result;
      RS.phrasesLoaded = false;
      return {};
    },
  });
}

window.HUD_REPORTS = {
  renderReports: renderMain,
  renderPatientReports,
  renderTemplates,
  initReports,
  invalidateReportsCache,
  recover: reportsRecover, // R3e
  unsentCount: reportsUnsentCount, // QS1/QS2
  dropGhost,               // R3f
  invalidateTemplatesCache,
  invalidatePhrasesCache,
  navToTemplates,
  openNewPhraseDialog,
  _openTemplatePickerForChild,
  _pdfToPatientFolder,
  makeReportPdfBlob, // report PDF v2 (download + share)
  loadPhrases,
  calcAge,
  // Intake→PDF (Track A) — generic PDF surface reused by the intake responses viewer (index.html).
  generatePDFBlob,
  _downloadBlob,
  getBranding,
  brandingForClient: async (ref) => brandingForClientRef(H()._supa, getBranding(), ref, DEFAULT_BRANDING),   // D-2 SC3: the editor + the intake PDF
  ensureBranding,
  reportColours, // Phase 2 Q5
  openSignaturePad, // Phase 3 R9
  removableSignaturePaths, // R-2 (queue 32)
  removableLogoPaths, // F-7
  recoverSignaturePath, signatureRetryFailed, // R-2 (QR2-3 B)
  RS,
};
